import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import { useUpdateExamPaper } from "@/apis/exam_papers/updateExamPaper";
import { Spin, message } from "antd";
import { ExamPaperForm } from "./components/ExamPaperForm";
import type { ICreateExamPaperRequest } from "@/apis/exam_papers/types";
import type { IUpdateExamPaperRequest } from "@/apis/exam_papers/updateExamPaper";

const ExamPaperEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Query exam paper data
  const { data: examPaper, isLoading, error } = useQueryGetExamPaper(id || "");

  // Update exam paper mutation
  const { mutate: updateExamPaper } = useUpdateExamPaper();

  // Go back to detail page
  const handleBack = () => {
    navigate(`/exam_papers/${id}`);
  };

  // Handle form submission
  const handleSubmit = async (data: ICreateExamPaperRequest) => {
    if (!examPaper) return;

    const changedData: IUpdateExamPaperRequest = {
      title: data.title,
      difficulty: data.difficulty,
      sections: data.sections.map((section) => ({
        duration: section.duration,
      })),
    };

    // Check if there are any changes
    const hasChanges =
      changedData.title !== examPaper.title ||
      changedData.difficulty !== (examPaper.difficulty || 0) ||
      (examPaper.sections && examPaper.sections.length >= 2
        ? changedData.sections[0]?.duration !==
            examPaper.sections[0]?.duration ||
          changedData.sections[1]?.duration !== examPaper.sections[1]?.duration
        : true);

    if (hasChanges) {
      setIsSubmitting(true);
      setSubmitError(null);

      updateExamPaper(
        { id: examPaper.id, data: changedData },
        {
          onSuccess: () => {
            navigate(`/exam_papers/${examPaper.id}`);
          },
          onError: (error: Error) => {
            setSubmitError(error.message);
            setIsSubmitting(false);
          },
        }
      );
    } else {
      // No changes, navigate back
      navigate(`/exam_papers/${examPaper.id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="h-screen flex items-center justify-center">
          <Spin size="large" tip="Loading..." />
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !examPaper) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam paper information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to Detail
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>
            <Heading>Edit Exam Paper</Heading>
          </div>
          <Text className="text-zinc-500">
            Update basic information about this exam paper.
          </Text>
        </div>

        {/* Form */}
        <ExamPaperForm
          initialData={{
            title: examPaper.title,
            subject: examPaper.subject,
            difficulty: examPaper.difficulty || 0,
            sections: examPaper.sections?.map((section) => ({
              duration: section.duration,
              question_count: section.question_ids?.length || 0,
            })) || [
              { duration: 35, question_count: 22 },
              { duration: 35, question_count: 22 },
            ],
          }}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          submitButtonText="Save Changes"
          isEditMode={true}
        />
      </div>
    </PageContainer>
  );
};

export default ExamPaperEditPage;
